import { useEffect, useState, useMemo } from "react";
import { Button, Row, Col, Container, FormFeedback, FormText, FormGroup, Input, InputGroup, InputGroupText, Label, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import _ from 'lodash';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "../../../hooks/useTheme";
import DataTable from "../../../components/DataTable";
import DialogBox from "../../../components/DialogBox";
import ItemTypeCustomAttributes from './ItemTypeCustomAttributes';
import { createItemLookupVersions, updateItemLookupVersions } from "../data/itemLookupVersionService";
import { addModuleRevisionLookupItem } from "../../modules/data/moduleRevisionLookupItemService";
import { createItemLookup, updateItemLookup } from "../data/itemLookupService";
import { createItemLookupTag, deleteItemLookupTags } from "../data/itemLookupTagService";
import { createAudit, fetchAuditByFilter } from "../../audit/data/auditService";
import { fetchUsers } from "../../users/data/userService";
import { formatDateTime } from "../../../utils/dateUtils";
import { formatCurrency } from "../../../utils/numberUtils";

import CustomSelect from "../../../components/CustomSelect";
import ItemVersionModal from "./ItemVersionModal";
import ItemCustomAttributes from "./ItemCustomAttributes";

const ItemModal = ({ toggle, item: initialItem, suppliersData, itemLookupTypesOptions, itemLookupTypesData, tagsData, onItemChange, moduleRevisionId, toggleFullscreen, fullscreen }) => {
    const theme = useTheme();

    // State for current active Tab
    const [currentActiveTab, setCurrentActiveTab] = useState("Details");

    // State for item (to update the displayed item dynamically)
    const [item, setItem] = useState(structuredClone(initialItem));
    const [originalItem, setOriginalItem] = useState(structuredClone(initialItem));

    //state for version
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
    const [isCreatingNewVersion, setIsCreatingNewVersion] = useState(false);

    // dialog box state
    const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);

    const customStyles = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    };

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }))
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["itemLookupUsers"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });

    // const getRlecCodes = async () => {
    //     const codes = await fetchSalesforceRLECCodes();
    //     return codes.data;
    // }

    // const {
    //     data: rlecCodes,
    //     isLoading: isLoadingSalesforceCodes,
    //     error: salesforceCodesError,
    // } = useQuery({
    //     queryKey: ["salesforceCodes"],
    //     queryFn: getRlecCodes,
    //     staleTime: 60000
    // });

    const getAudit = async () => {
        const filter = { "pageNumber": 0, "pageSize": 0, "source": `item.${item.id}` }
        const audits = await fetchAuditByFilter(filter);

        return audits.auditItems.map((audit) => {
            const user = usersLookup.find((user) => user?.userGuid === audit.userGuid);
            return {
                ...audit,
                timestamp: formatDateTime(audit.timestamp),
                user: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            };
        });
    }

    const {
        data: auditData,
        isLoading: isLoadingAudits,
        error: auditError,
        refetch: refetchAudits,
    } = useQuery({
        queryKey: ["itemLookupAudits"],
        queryFn: getAudit,
        enabled: !!usersLookup, // Only run when usersLookup are available
        staleTime: 60000
    });

    useEffect(() => {
        if (usersLookup) {
            refetchAudits();
        }
    }, [usersLookup]);

    useEffect(() => {
        setIsVersionModalOpen(true);
    }, [selectedVersion])

    // Handle input changes
    const handleChange = async (e) => {
        console.log(e);
        const { name, value, type, checked } = e.target;
        if (currentActiveTab === "Commercials") {
            setItem(prevItem => ({
                ...prevItem,
                versions: prevItem.versions.map(version =>
                    version.id === prevItem.activeVersion
                        ? { ...version, [name]: type === "checkbox" ? checked : (value.trim() === "" ? null : value) }
                        : version
                )
            }));
        } else {
            setItem(prevItem => ({
                ...prevItem,
                [name]: type === "checkbox" ? checked : (value.trim() === "" ? null : value)
            })
            );
        }
    };

    const handleSupplierChange = (e) => {
        setItem(prevItem => ({ ...prevItem, supplierId: e.value, supplierName: e.label }))
    }

    const handleItemLookupTypeChange = (e) => {
        setItem(prevItem => ({ ...prevItem, itemLookupTypeId: e.value, itemLookupTypeName: e.label, typeCustomAttributes: [] }))
    }

    const handleTagChange = (selectedOptions) => {
        setItem({ ...item, tags: selectedOptions.map((tag) => ({ id: tag.value, tagName: tag.label })) })
    }

    const limitDecimals = (value, maxDecimals) => {
        const valueToString = value.toString();
        return parseFloat(valueToString.slice(0, valueToString.indexOf(".") + (maxDecimals + 1)));
    }

    const handleTypeCustomAttributesChange = (e, type, maxDecimals = null) => {
        let typeCustomAttributes = item.typeCustomAttributes ? [...item.typeCustomAttributes] : [];
        const label = e.target.name;
        let newValue;

        if (maxDecimals !== null) {
            e.target.value = limitDecimals(e.target.value, maxDecimals);
        }

        switch (type) {
            case 'integer':
                newValue = e.target.value === '' ? '' : e.target.valueAsNumber;
                break;
            case 'boolean':
                newValue = e.target.checked;
                break;
            case 'decimal':
                newValue = parseFloat(e.target.value) || 0;
                break;
            default:
                newValue = e.target.value;
        }

        if (newValue === '' && (type === 'text' || type === 'integer' || type === 'lookup')) {
            typeCustomAttributes = typeCustomAttributes.filter(attr => attr.label !== label);
        } else {
            const index = typeCustomAttributes.findIndex(attr => attr.label === label);
            if (index !== -1) {
                typeCustomAttributes[index] = {
                    ...typeCustomAttributes[index],
                    value: newValue,
                };
            } else {
                typeCustomAttributes.push({ label, value: newValue });
            }
        }

        const updatedItem = { ...item, typeCustomAttributes };
        setItem(updatedItem);
    };

    const saveChanges = async () => {
        let itemToSave = { ...item }

        if (!closestPastVersion?.isUpfront) itemToSave.upfrontSalesforceCode = null;
        if (!closestPastVersion?.isRecurring) itemToSave.recurringSalesforceCode = null;

        // Re-derive salesforceCode from the remaining codes
        const upCode = itemToSave.upfrontSalesforceCode;
        const recCode = itemToSave.recurringSalesforceCode;
        if (upCode && recCode && upCode !== recCode) {
            itemToSave.salesforceCode = `${upCode} / ${recCode}`;
        } else {
            itemToSave.salesforceCode = upCode || recCode || null;
        }
        
        if (_.isEqual(itemToSave, originalItem)) {
            toggle();
            return
        }


        if (!validateItem(itemToSave)) return false;

        setTypeCustomAttributesDefaultValues(itemToSave);

        const isNewItem = item.id === 0
        let auditChanges = [];

        const saveItemIsOK = await saveMainItem(itemToSave, isNewItem, auditChanges);
        if (!saveItemIsOK) return;
        await saveItemLookupTags(itemToSave, isNewItem, auditChanges);
        await saveVersions(itemToSave, auditChanges);
        await saveAudit(itemToSave, isNewItem, auditChanges);
        
        await saveModuleRevisionItemLookup(itemToSave);

        setItem(itemToSave);
        setOriginalItem(itemToSave); //update the original item in memory to compare with future changes
        onItemChange(itemToSave, isNewItem);

        toggle();
    }

    const toCustomAttrArray = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === "string") {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed : [];
            } catch { return []; }
        }
        return []; // null, undefined, object, etc.
    };

    const validateItem = (item) => {
        let blankFields = []
        if (!item.name) blankFields.push("Name");
        if (!item.autotaskCode) blankFields.push("Autotask Code");
        if (!item.itemLookupTypeId) blankFields.push("Type");
        if (!item.supplierId) blankFields.push("Supplier");
        if (item.typeCustomAttributes)
            item.typeCustomAttributes.forEach(attribute => {
                var foundLabelInCustomAttributes = item.typeCustomAttributes.find(w => w.label === attribute.label)
                if (attribute.required && !foundLabelInCustomAttributes) {
                    if (attribute.defaultValue !== null) {
                        item.typeCustomAttributes.push({ label: attribute.label, value: attribute.defaultValue })
                    } else {
                        if (attribute.fieldType === "boolean")
                            item.typeCustomAttributes.push({ label: attribute.label, value: false })
                        else
                            blankFields.push(attribute.label);
                    }
                }
            }
            );

        if (blankFields.length > 0) {
            toast.warning(`Please fill in the following fields: ${blankFields.join(", ")}`);
            return false
        }
        return true
    }

    const saveModuleRevisionItemLookup = async (item) => {
        // If you were to be adding this item lookup from the module builder rather than the admin items list, then we
        // need to add this moduleRevision to the list of moduleRevisions that are using this item.
        if (!item.moduleRevisionLookupItems.some(x => x.moduleRevisionId === moduleRevisionId)) {
            item.moduleRevisionLookupItems.push({ moduleRevisionId: moduleRevisionId })
            await addModuleRevisionLookupItem(moduleRevisionId, item.id)
        }
    }

    const saveMainItem = async (item, isNewItem, auditChanges) => {
        let cleanItem = { ...item };
        delete cleanItem.tags
        delete cleanItem.versions;
        delete cleanItem.activeVersion;

        let cleanOriginalItem = { ...originalItem };
        delete cleanOriginalItem.tags
        delete cleanOriginalItem.versions;
        delete cleanOriginalItem.activeVersion;

        if (!_.isEqual(cleanItem, cleanOriginalItem)) {
            if (isNewItem) {
                auditItemChanges(true, auditChanges)
                delete cleanItem.id // delete the id before sending to ItemLookupDTO in the backend
                if (item.customAttributes) cleanItem.customAttributes = JSON.parse(item.customAttributes)
                const response = (await createItemLookup(cleanItem)).data;
                if (response.newId) {
                    item.id = response.newId;
                    item.createdAt = formatDateTime(response.createdAt);
                } else {
                    toast.warning(`Error: ${response}`);
                    return false;
                }
            } else {                    // existing item
                auditItemChanges(false, auditChanges)
                if (item.customAttributes) cleanItem.customAttributes = JSON.parse(item.customAttributes)
                const response = (await updateItemLookup(cleanItem.id, cleanItem)).data;
                if (response.updatedAt) {
                    item.updatedAt = formatDateTime(response.updatedAt);
                } else {
                    toast.warning(`Error: ${response}`);
                    return false;
                }
            }
        }

        return true;
    }

    const setTypeCustomAttributesDefaultValues = (itemToSave) => {
        const typeCustomAttributesWithDefaultValues = itemLookupTypesData.filter(w => w.id === itemToSave.itemLookupTypeId)[0]?.customAttributes?.filter(w => w.defaultValue)
        if (!typeCustomAttributesWithDefaultValues) return;

        typeCustomAttributesWithDefaultValues.forEach(typeAttribute => {
            const itemTypeCustomAttribute = itemToSave.typeCustomAttributes?.find(w => w.label === typeAttribute.label)
            if (!itemTypeCustomAttribute) {
                itemToSave.typeCustomAttributes.push({ label: typeAttribute.label, value: typeAttribute.defaultValue })
            }
            else if (itemTypeCustomAttribute.value === null || itemTypeCustomAttribute.value === undefined) {
                itemTypeCustomAttribute.value = typeAttribute.defaultValue
            }
        })

        setOriginalItem(itemToSave);
    }

    const saveItemLookupTags = async (item, isNewItem, auditChanges) => {
        if (!_.isEqual(item.tags, originalItem.tags)) {
            await deleteItemLookupTags(item.id);

            await Promise.all(
                item.tags.map(async (tag) => {
                    await createItemLookupTag({ itemLookupId: item.id, tagId: tag.id });
                })
            );

            auditTagChanges(isNewItem, auditChanges)
        }
    }

    const saveVersions = async (item, auditChanges) => {
        if (!item.versions || item.versions.length === 0) return

        // update existing versions in a batch
        let futureVersions = item.versions.filter(v => v.id > 0 && v.state === "future").sort((a, b) => a.id - b.id);
        if (futureVersions.length > 0) {
            let versionsToUpdate = []
            for (const version of futureVersions) {
                const originalVersion = originalItem.versions.find(w => w.id === version.id);
                if (!_.isEqual(version, originalVersion)) {
                    auditItemVersionChanges(version, originalVersion, false, auditChanges);
                    versionsToUpdate.push(version);
                }
            }
            await updateItemLookupVersions(versionsToUpdate);
        }

        // save new versions in a batch
        let newVersions = item.versions.filter(v => v.id < 0).sort((a, b) => b.id - a.id);
        if (newVersions.length > 0) {
            if (item.activeVersion <= 0) { // if it is a new item, add the item id to the new versions 
                newVersions = newVersions.map(v => ({ ...v, itemLookupId: item.id }))
            }
            const newVersionsSaved = (await createItemLookupVersions(newVersions)).data.savedItems;
            for (const version of newVersionsSaved) {
                newVersions.find(nv => nv.versionNumber === version.versionNumber).id = version.id // replace the negative id with the saved id
                auditItemVersionChanges(version, null, true, auditChanges)
            }
        }

        const closestPastVersion = getClosestPastVersion(item.versions)
        if (closestPastVersion !== undefined) {
            item.activeVersion = closestPastVersion.id
        }
    }

    const saveAudit = async (item, isNewItem, auditChanges) => {
        if (auditChanges.length === 0) return

        const auditEntry = "{ " + (auditChanges.join(", ")) + " }";
        const action = isNewItem ? "Item created" : "Item changed";
        await createAudit({ "action": action, "result": "Successful", "source": `item.${item.id}`, "payload": auditEntry })
    }

    const stringifyCustomAttributes = (customAttributes, keyProp, valueProp) => {
        if (!customAttributes || customAttributes.length === 0) return null;

        customAttributes = typeof (customAttributes) === "string" ? JSON.parse(customAttributes) : customAttributes;

        customAttributes = customAttributes.reduce((acc, customAttributes) => {
            const key = String(customAttributes[keyProp]);
            acc[key] = customAttributes[valueProp];
            return acc;
        }, {});

        return JSON.stringify(customAttributes);
    }

    const auditItemChanges = (isNewItem, auditChanges) => {
        const auditFieldsToCompare = ["name", "options", "itemLookupTypeName", "supplierName", "autotaskCode", "salesforceCode", "isInternal",
            "notes", "customAttributes", "typeCustomAttributes", "hideFromUsers", "disabled", "exemptFromMarginWarnings", "hideOnQuoteByDefault", "isCreditItem"]
        auditFieldsToCompare.forEach((field) => {
            let fieldDisplayName = field
            if (field === "itemLookupTypeName") {
                fieldDisplayName = "type";
            } else if (field === "supplierName") {
                fieldDisplayName = "supplier";
            }
            let originalFieldValue, currentFieldValue

            if (field === "customAttributes") {
                originalFieldValue = stringifyCustomAttributes(originalItem[field], "label", "defaultValue")
                currentFieldValue = stringifyCustomAttributes(item[field], "label", "defaultValue")
            }
            else if (field === "typeCustomAttributes") {
                originalFieldValue = stringifyCustomAttributes(originalItem[field], "label", "value")
                currentFieldValue = stringifyCustomAttributes(item[field], "label", "value")
            }
            else {
                originalFieldValue = originalItem[field] === null ? null : JSON.stringify(originalItem[field])
                currentFieldValue = item[field] === null ? null : JSON.stringify(item[field])
            }

            if (originalFieldValue === null && currentFieldValue === null) return;
            currentFieldValue = sanitiseAttributes(currentFieldValue)

            if (!isNewItem) {
                if (currentFieldValue !== originalFieldValue) {
                    var changes = `\"${fieldDisplayName}\": { \"new\": ${currentFieldValue}, \"old\": ${originalFieldValue} }`
                    auditChanges.push(changes)
                }
            } else {
                if (currentFieldValue !== null) {
                    auditChanges.push(`\"${fieldDisplayName}\": ${currentFieldValue}`)
                }
            }
        })
    }

    const sanitiseAttributes = (value) => {
        const emptyValues = ["{}", "[]", "", " "];
        return emptyValues.includes(value) ? null : value;
    };

    const auditTagChanges = (isNewItem, auditChanges) => {
        const field = "tags"
        const originalField = JSON.stringify(originalItem[field])
        const currentField = JSON.stringify(item[field])

        if (!isNewItem) {
            if (item[field] !== originalItem[field]) {
                auditChanges.push(`\"${field}\": {\"new\": ${currentField}, \"old\": ${originalField}}`)
            }
        } else {
            auditChanges.push(`\"${field}\": ${JSON.stringify(item[field])}`)
        }
    }

    const auditItemVersionChanges = (currentVersion, originalVersion, isNewVersion, auditChanges) => {
        let versionChanges = []
        const auditFieldsToCompare = ["quoteUpfrontDescription", "quoteRecurringDescription", "isUpfront", "upfrontCost", "upfrontPrice", "upfrontCostDefaultDiscount", "upfrontCostDiscountThreshold", "upfrontPriceDefaultDiscount", "upfrontPriceDiscountThreshold",
            "isRecurring", "recurringCost", "recurringPrice", "recurringCostDefaultDiscount", "recurringCostDiscountThreshold", "recurringPriceDefaultDiscount", "recurringPriceDiscountThreshold", "recurringIntervalMonths", "recurringNumPayments", "recurringStartMonth",
            "upfrontSalesforceCode", "recurringSalesforceCode", "activeStartDate"
        ]

        versionChanges.push(`\"versionNumber\": ${currentVersion.versionNumber}`)
        versionChanges.push(`\"isNew\": ${isNewVersion}`)

        auditFieldsToCompare.forEach((field) => {
            let fieldDisplayName = field
            if (field === "quoteUpfrontDescription") {
                fieldDisplayName = "upfrontDescription";
            } else if (field === "quoteRecurringDescription") {
                fieldDisplayName = "recurringDescription";
            }

            const currentFieldValue = JSON.stringify(currentVersion[field])

            if (isNewVersion) {
                versionChanges.push(`\"${fieldDisplayName}\": ${currentFieldValue}`)
            } else {
                const originalFieldValue = JSON.stringify(originalVersion[field])
                if (currentFieldValue !== originalFieldValue) {
                    versionChanges.push(`\"${fieldDisplayName}\": {\"new\": ${currentFieldValue}, \"old\": ${originalFieldValue}}`)
                }
            }
        })
        auditChanges.unshift(versionChanges) // audit the version changes first
    }

    console.log("item:", item);

    let selectedItemLookupType = useMemo(() => item.itemLookupTypeId ?
        itemLookupTypesData.find(f => f.id === item.itemLookupTypeId)
        : null, [item.itemLookupTypeId, itemLookupTypesData]);

    const onCustomAttributesChange = (newCustomAttributes) => {
        setItem(prevItem => ({
            ...prevItem,
            customAttributes: newCustomAttributes
        }));
    }

    // Define History columns
    const historyColumns = [
        {
            key: "timestamp",
            label: "Timestamp"
        },
        { key: "user", label: "User" },
        { key: "action", label: "Action" },
        { key: "notes", label: "Notes" },
        { key: "payload", label: "Payload" }
    ];

    const handleClose = () => {
        if (!_.isEqual(item, originalItem)) {
            openDialogBox();
        } else {
            toggle();
        }
    }

    const openDialogBox = () => setIsDialogBoxOpen(true);
    const closeDialogBox = () => setIsDialogBoxOpen(false);


    // Find the closest past version
    const today = new Date();

    const getClosestPastVersion = (versions) =>
        versions.filter(r => new Date(r.activeStartDate) < new Date(Date.now()))
            .sort((a, b) => {
                const startDiff = new Date(b.activeStartDate) - new Date(a.activeStartDate);
                if (startDiff !== 0) return startDiff;
                return new Date(b.createdAt) - new Date(a.createdAt);
            })[0] || null;

    const closestPastVersion = getClosestPastVersion([...item.versions])

    const getTypeCustomAttributeValue = (field) => {
        const typeCustomAttributes = item.typeCustomAttributes?.find(attr => attr.label === field.label)
        return typeCustomAttributes ? typeCustomAttributes.value : field.defaultValue || null;
    }

    // THC - Temporary fix for customAttributes being a string instead of an array
    useEffect(() => {
        if (typeof selectedItemLookupType?.customAttributes === "string") {
            selectedItemLookupType.customAttributes = JSON.parse(selectedItemLookupType.customAttributes)
        }
        console.log("Selected Item Lookup Type changed:", selectedItemLookupType);
    }, [selectedItemLookupType])

    return (
        <>
            <div className="modal-header">
                <h5 className="mb-0">{item.itemLookup?.name || "Item Details"}</h5>
                <div className="d-flex gap-2">
                    <Button style={{ borderColor: 'transparent' }} color={theme === "dark-mode" ? "dark" : "light"} outline onClick={toggleFullscreen}><i className={"bi".concat(fullscreen ? " bi-fullscreen-exit" : " bi-arrows-fullscreen")}></i></Button>
                    <Button style={{ borderColor: 'transparent' }} color={theme === "dark-mode" ? "dark" : "light"} outline onClick={handleClose}><i className="bi bi-x"></i></Button>
                </div>
            </div>
            <ModalBody style={{ height: '60vh' }}>
                {/* Navigation */}
                <Row className="modal-menu">
                    <Col style={{ display: 'inline-flex' }}>
                        {(selectedItemLookupType && selectedItemLookupType.customAttributes ? ["Details", selectedItemLookupType.itemLookupTypeName, "Commercials", "Additional", "Custom"] : ["Details", "Commercials", "Additional", "Custom"]).map((tab) => (
                            <h6
                                key={tab}
                                className={currentActiveTab === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setCurrentActiveTab(tab)}
                            >
                                {tab}
                            </h6>
                        ))}
                    </Col>
                    <Col className="text-end">
                        <h6
                            className={currentActiveTab === "History" ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                            style={{ cursor: 'pointer', display: 'inline-flex' }}
                            onClick={() => setCurrentActiveTab("History")}
                        >
                            History
                        </h6>
                    </Col>
                </Row>
                <br />

                {/* Details Tab */}
                {currentActiveTab === "Details" && (
                    <Container fluid>
                        <FormGroup row>
                            <Label for="name" sm={2}>Name</Label>
                            <Col sm={10}>
                                <Input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={item.name || ""}
                                    onChange={handleChange}
                                    invalid={!item.name}
                                />
                                <FormFeedback>Name is required.</FormFeedback>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="options" sm={2}>Options</Label>
                            <Col sm={10}>
                                <Input
                                    type="text"
                                    name="options"
                                    id="options"
                                    value={item.options || ""}
                                    onChange={handleChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="autotaskCode" sm={2}>Autotask Code</Label>
                            <Col sm={10}>
                                <Input
                                    type="text"
                                    name="autotaskCode"
                                    id="autotaskCode"
                                    value={item.autotaskCode || ""}
                                    onChange={handleChange}
                                    invalid={!item.autotaskCode}
                                />
                                <FormFeedback>Autotask Code is required.</FormFeedback>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="supplier" sm={2}>Supplier</Label>
                            <Col sm={10}>
                                <Select
                                    id="supplier"
                                    isSearchable={true}
                                    options={
                                        [...suppliersData].sort((a, b) =>
                                            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                                        )
                                    }
                                    classNamePrefix="data-select"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        ...customStyles,
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        menu: base => ({ ...base, zIndex: 9999 })
                                    }}
                                    value={item.supplierId ? { value: item.supplierId, label: item.supplierName } : null}
                                    theme={customTheme}
                                    onChange={handleSupplierChange}
                                    isDisabled={false}
                                />
                                {!item.supplierId && (
                                    <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                        Supplier is required.
                                    </div>
                                )}
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label for="type" sm={2}>Type</Label>
                            <Col sm={10}>
                                <Select
                                    id="type"
                                    isSearchable={true}
                                    options={
                                        [...itemLookupTypesOptions].sort((a, b) =>
                                            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                                        )
                                    }
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        ...customStyles,
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        menu: base => ({ ...base, zIndex: 9999 })
                                    }}
                                    classNamePrefix="data-select"
                                    value={item.itemLookupTypeId ? { value: item.itemLookupTypeId, label: item.itemLookupTypeName } : null}
                                    theme={customTheme}
                                    onChange={handleItemLookupTypeChange}
                                    isDisabled={false}
                                />
                                {!item.itemLookupTypeId && (
                                    <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                        Type is required.
                                    </div>
                                )}
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label sm={2}>Tags</Label>
                            <Col sm={10}>
                                <CustomSelect
                                    isMulti
                                    creatable
                                    options={
                                        tagsData.map((item) => ({
                                            value: item.id,
                                            label: item.tagName
                                        }))
                                    }
                                    onChange={(selectedOptions) => handleTagChange(selectedOptions)}
                                    defaultValue={item.tags.map((item) => ({
                                        value: item.id,
                                        label: item.tagName
                                    }))}
                                />
                            </Col>
                        </FormGroup>
                    </Container>
                )}

                {selectedItemLookupType !== null && selectedItemLookupType.customAttributes !== null && selectedItemLookupType.customAttributes.length > 0 && Array.isArray(selectedItemLookupType?.customAttributes) && currentActiveTab === selectedItemLookupType.itemLookupTypeName &&
                    <Container fluid>
                        {selectedItemLookupType.customAttributes.map((field, index) => {
                            return (
                                <FormGroup row>
                                    <Label for="type" sm={2}>{field.label}</Label>
                                    <Col sm={10}>
                                        <ItemTypeCustomAttributes key={index} label={field.label} type={field.fieldType} required={field.required} handleChange={handleTypeCustomAttributesChange} value={getTypeCustomAttributeValue(field)} lookupChoices={field.lookupChoices} />
                                    </Col>
                                </FormGroup>
                            );
                        })}
                    </Container>
                }

                {/* Commercials Tab */}
                {currentActiveTab === "Commercials" && (
                    <Container fluid>
                        <DataTable
                            className="mb-5 pb-5"
                            sortByColumn={"activeStartDate"}
                            sortDirection="descending"
                            columns={[
                                // {
                                //     key: "versionNumber",
                                //     label: "Version",
                                //     render: (value, row) => {
                                //         return <div>{row.versionNumber}</div>
                                //     }
                                // },
                                // {
                                //     key: "state",
                                //     label: "State",
                                //     render: (value, row) => {
                                //         return <div>{String(row.state[0]).toUpperCase() + String(row.state).slice(1)}</div>
                                //     }
                                // },
                                {
                                    key: "activeStartDateStar",
                                    label: "",
                                    render: (value, row) => (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {closestPastVersion?.id === row.id && (
                                                <i className="bi bi-star-fill"></i>
                                            )}
                                        </div>
                                    ),
                                },
                                {
                                    key: "activeStartDate",
                                    label: "Active Start Date",
                                    style: { width: "200px" },
                                    render: (value, row) => {
                                        return <div>{formatDateTime(new Date(value))}</div>
                                    }
                                },
                                {
                                    key: "Recurrence",
                                    label: "Recurrence",
                                    style: { width: "140px" },
                                    render: (value, row) => {
                                        const isUpfront = row.isUpfront;
                                        const isRecurring = row.isRecurring;

                                        let typeLabel = "";
                                        if (isUpfront && isRecurring) {
                                            typeLabel = "Both";
                                        } else if (isUpfront) {
                                            typeLabel = "Upfront";
                                        } else if (isRecurring) {
                                            typeLabel = "Recurring";
                                        }

                                        return <div>{typeLabel}</div>;
                                    }
                                },
                                ...(!item.versions.every(r => parseFloat(r.upfrontCost || 0) === 0) ? [{
                                    key: "upfrontCost",
                                    label: "Upfront Cost",
                                    render: (value) => (
                                        <InputGroup>
                                            <InputGroupText>£</InputGroupText>
                                            <Input disabled className="px-3 text-end" type="text"
                                                value={formatCurrency(value)} />
                                        </InputGroup>
                                    )

                                }] : []),
                                ...(!item.versions.every(r => parseFloat(r.upfrontPrice || 0) === 0) ? [{
                                    key: "upfrontPrice",
                                    label: "Upfront Price",
                                    render: (value) => (
                                        <InputGroup>
                                            <InputGroupText>£</InputGroupText>
                                            <Input disabled className="px-3 text-end" type="text"
                                                value={formatCurrency(value)} />
                                        </InputGroup>
                                    )
                                }] : []),
                                ...(!item.versions.every(r => parseFloat(r.recurringCost || 0) === 0) ? [{
                                    key: "recurringCost",
                                    label: "Recurring Cost",
                                    render: (value) => (
                                        <InputGroup>
                                            <InputGroupText>£</InputGroupText>
                                            <Input disabled className="px-3 text-end" type="text"
                                                value={formatCurrency(value)} />
                                        </InputGroup>
                                    )
                                }] : []),
                                ...(!item.versions.every(r => parseFloat(r.recurringPrice || 0) === 0) ? [{
                                    key: "recurringPrice",
                                    label: "Recurring Price",
                                    render: (value) => (
                                        <InputGroup>
                                            <InputGroupText>£</InputGroupText>
                                            <Input disabled className="px-3 text-end" type="text"
                                                value={formatCurrency(value)} />
                                        </InputGroup>
                                    )
                                }] : [])
                            ]}
                            data={item.versions}
                            exportToCsv={false}
                            isLoading={isLoadingAudits || isLoadingUsersLookup}
                            error={auditError || usersLookupError}
                            actions={[
                                <Button key="add-version" onClick={() => {
                                    setIsCreatingNewVersion(true);
                                    let newVersionId = -1
                                    let newVersionNumber = 1
                                    let currentVersion = null

                                    if (item.versions.length > 0) {
                                        const newVersions = item.versions.filter(r => r.id <= 0); // the new versions will contain negative ids until they are saved                                        
                                        if (newVersions.length > 0) {
                                            const newestVersion = newVersions.reduce((latest, current) => {
                                                return current.id < latest.id ? current : latest
                                            });
                                            newVersionId = parseInt(newestVersion.id) - 1
                                        }

                                        const latestVersionNumber = item.versions.reduce((latest, current) => {
                                            return current.versionNumber > latest.versionNumber ? current : latest
                                        });
                                        newVersionNumber = parseInt(latestVersionNumber.versionNumber) + 1

                                        currentVersion = item.versions.find(r => r.id === item.activeVersion);
                                    }

                                    const newVersion = {
                                        id: newVersionId,
                                        itemLookupId: item.id,
                                        versionNumber: newVersionNumber,
                                        isUpfront: currentVersion ? currentVersion.isUpfront : false,
                                        quoteUpfrontDescription: currentVersion ? currentVersion.quoteUpfrontDescription : null,
                                        upfrontCost: currentVersion ? currentVersion.upfrontCost : null,
                                        upfrontPrice: currentVersion ? currentVersion.upfrontPrice : null,
                                        upfrontCostDefaultDiscount: currentVersion ? currentVersion.upfrontCostDefaultDiscount : null,
                                        upfrontCostDiscountThreshold: currentVersion ? currentVersion.upfrontCostDiscountThreshold : null,
                                        upfrontPriceDefaultDiscount: currentVersion ? currentVersion.upfrontPriceDefaultDiscount : null,
                                        upfrontPriceDiscountThreshold: currentVersion && currentVersion.upfrontPriceDiscountThreshold !== null ? currentVersion.upfrontPriceDiscountThreshold : 20,
                                        upfrontSalesforceCode: currentVersion ? currentVersion.upfrontSalesforceCode : null,
                                        upfrontRecognitionMonths: currentVersion ? currentVersion.upfrontRecognitionMonths : null,
                                        isRecurring: currentVersion ? currentVersion.isRecurring : false,
                                        quoteRecurringDescription: currentVersion ? currentVersion.quoteRecurringDescription : null,
                                        recurringCost: currentVersion ? currentVersion.recurringCost : null,
                                        recurringPrice: currentVersion ? currentVersion.recurringPrice : null,
                                        recurringCostDefaultDiscount: currentVersion ? currentVersion.recurringCostDefaultDiscount : null,
                                        recurringCostDiscountThreshold: currentVersion ? currentVersion.recurringCostDiscountThreshold : null,
                                        recurringPriceDefaultDiscount: currentVersion ? currentVersion.recurringPriceDefaultDiscount : null,
                                        recurringPriceDiscountThreshold: currentVersion && currentVersion.recurringPriceDiscountThreshold !== null ? currentVersion.recurringPriceDiscountThreshold : 20,
                                        recurringIntervalMonths: currentVersion ? currentVersion.recurringIntervalMonths : null,
                                        recurringNumPayments: currentVersion ? currentVersion.recurringNumPayments : null,
                                        recurringStartMonth: currentVersion ? currentVersion.recurringStartMonth : null,
                                        recurringSalesforceCode: currentVersion ? currentVersion.recurringSalesforceCode : null,
                                        activeStartDate: new Date(),
                                        createdAt: new Date(),
                                        updatedAt: null,
                                        disabled: false
                                    }
                                    setSelectedVersion(newVersion);
                                }}>
                                    Add New Version
                                </Button>
                            ]}
                            onRowClick={(row) => {
                                setSelectedVersion(row);
                            }}
                        ></DataTable>
                        <hr />
                        <FormGroup row>

                            <Label sm={2}>Is Internal?</Label>
                            <Col xs="auto">
                                <Input
                                    name="isInternal"
                                    type="checkbox"
                                    checked={item.isInternal}
                                    onChange={(e) => setItem(prev => ({ ...prev, isInternal: e.target.checked }))}
                                />
                            </Col>
                            <Col>
                                <FormText sm={8}><i className="bi bi-info-circle-fill me-2"></i>If checked, then this is a Maintel Item. Otherwise, it's from a third party supplier</FormText>
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Label sm={2}>Is Credit Item?</Label>
                            <Col xs="auto">
                                <Input
                                    name="isCreditItem"
                                    type="checkbox"
                                    checked={item.isCreditItem}
                                    onChange={(e) => setItem(prev => ({ ...prev, isCreditItem: e.target.checked }))}
                                />
                            </Col>
                            <Col>
                                <FormText sm={8}><i className="bi bi-info-circle-fill me-2"></i>If checked, this will produce negative quantities and costs/prices.  Typically used for changing an order, or for refunds</FormText>
                            </Col>
                        </FormGroup>
                    </Container>
                )}

                {/* Additional Tab */}
                {currentActiveTab === "Additional" && (
                    <Container fluid>
                        <FormGroup row>
                            <Label sm={2}>Notes</Label>
                            <Col sm={10}>
                                <Input
                                    type="textarea"
                                    name="notes"
                                    value={item.notes}
                                    onChange={handleChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label sm={2}>Disabled</Label>
                            <Col sm="auto" >
                                <Input
                                    type="checkbox"
                                    name="disabled"
                                    checked={item.disabled}
                                    onChange={handleChange}
                                />
                                <FormText><i className="bi bi-info-circle-fill ms-4 me-2"></i>If checked, this item will not be available to users or for use in modules</FormText>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label sm={2}>Internal Use Only</Label>
                            <Col sm="auto">
                                <Input
                                    type="checkbox"
                                    name="hideFromUsers"
                                    checked={item.hideFromUsers}
                                    onChange={handleChange}
                                />
                                <FormText><i className="bi bi-info-circle-fill ms-4 me-2"></i>If checked, this item will be unavailable to standard users. It will still be available for product modules and basket processing.</FormText>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label sm={2}>Hide on quote document by default</Label>
                            <Col sm="auto">
                                <Input
                                    type="checkbox"
                                    name="hideOnQuoteByDefault"
                                    checked={item.hideOnQuoteByDefault}
                                    onChange={handleChange}
                                    invalid={closestPastVersion ? closestPastVersion.isUpfront && closestPastVersion.upfrontPrice > 0 || closestPastVersion.isRecurring && closestPastVersion.recurringPrice > 0 : false}
                                />
                                <FormText><i className="bi bi-info-circle-fill ms-4 me-2"></i>If checked, this item will not appear on generated quote documents by default, but the user can override this.  This setting only applies if the price is £0</FormText>
                                <FormFeedback>This item currently has a price.  So this setting will be ignored</FormFeedback>
                            </Col>
                        </FormGroup>

                        <FormGroup row>
                            <Label sm={2}>Exempt from margin warnings</Label>
                            <Col sm="auto">
                                <Input
                                    type="checkbox"
                                    name="exemptFromMarginWarnings"
                                    checked={item.exemptFromMarginWarnings}
                                    onChange={handleChange}
                                />
                                <FormText><i className="bi bi-info-circle-fill ms-4 me-2"></i>If checked, this item will be exempt from triggering margin validation or low-margin alerts.</FormText>
                            </Col>
                        </FormGroup>
                    </Container>
                )}

                {/* Custom Attributes Tab */}
                {currentActiveTab === "Custom" && (
                    <ItemCustomAttributes json={item.customAttributes} onChange={(e) => onCustomAttributesChange(e)} />
                )}

                {/* History Tab */}
                {currentActiveTab === "History" && (
                    <Container fluid>
                        <DataTable
                            className="mb-5 pb-5"
                            columns={historyColumns}
                            data={auditData ?? []}
                            exportToCsv={true}
                            csvFileName="audit_items.csv"
                            sortByColumn="timestamp"
                            sortDirection="descending"
                            isLoading={isLoadingAudits || isLoadingUsersLookup}
                            error={auditError || usersLookupError}
                            searchableFields={["timestamp", "user", "action", "notes", "payload"]}
                        />
                        <br />
                        <br />
                    </Container>
                )}
            </ModalBody>
            <ModalFooter>
                <Button outline onClick={handleClose} color={theme === "dark-mode" ? "dark" : "light"}>
                    Close
                </Button>
                <Button color="secondary" onClick={saveChanges}>Save</Button>
            </ModalFooter>
            <ItemVersionModal
                isOpen={isVersionModalOpen}
                toggle={() => {
                    setIsVersionModalOpen(false);
                    setIsCreatingNewVersion(false);
                    setSelectedVersion(null);
                }}
                version={selectedVersion}
                isEditable={isCreatingNewVersion || selectedVersion?.state === "future"}
                onSave={(version) => {
                    const isNewVersion = version.id < 0 && version.state !== "future";

                    let updatedVersions = isNewVersion ? [...item.versions, version] :
                        item.versions.map(ver => ver.id !== version.id ? { ...ver } : version)

                    const updatedClosestPastVersion = getClosestPastVersion(updatedVersions);
                    const updatedClosestPastVersionId = updatedClosestPastVersion ? updatedClosestPastVersion.id : 0

                    const isCurrent = version.id === updatedClosestPastVersionId;
                    version.state = isCurrent ? "current" : new Date(version.activeStartDate) > new Date() ? "future" : "locked";

                    updatedVersions = updatedVersions.map(rev => {
                        const isFormerCurrent = rev.state === "current" && rev.id !== updatedClosestPastVersionId;
                        return {
                            ...rev,
                            state: isFormerCurrent ? "locked" : rev.state
                        };
                    });

                    const updatedItem = {
                        ...item,
                        versions: updatedVersions,
                        activeVersion: updatedClosestPastVersionId
                    };
                    setItem(updatedItem);
                    setIsVersionModalOpen(false);
                    setIsCreatingNewVersion(false);
                }}
            />

            <DialogBox
                isOpen={isDialogBoxOpen}
                title="Confirmation"
                message={
                    <div className="d-flex align-items-start">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <span>You have unsaved changes. <br />Save changes before closing?</span>
                    </div>
                }
                toggle={closeDialogBox}
                buttons={[
                    {
                        label: 'Yes',
                        color: 'primary',
                        // icon: 'bi bi-check2-circle',
                        onClick: () => { saveChanges(); closeDialogBox(); }
                    },
                    {
                        label: 'No',
                        color: 'secondary',
                        // icon: 'bi bi-x-circle',
                        onClick: () => { closeDialogBox(); toggle(); }
                    },
                    {
                        label: 'Cancel',
                        color: 'secondary',
                        onClick: () => { closeDialogBox(); }
                    }
                ]}
            />

        </>
    );
};

export default ItemModal;
